import * as XLSX from 'xlsx';
import * as FileSaver from 'file-saver';
import { PortvilleRoomWithResidency } from '../types/portvilleRoom';
import { PortvilleUsageRecord, UsageRecordFormData } from '../types/portvilleBilling';

/**
 * 엑셀 템플릿 다운로드용 데이터 타입
 */
export interface UsageExcelTemplate {
  호실번호: string;
  입주자명: string;
  소속: string;
  개별전기사용량_kWh: number | '';
  개별수도사용량_톤: number | '';
  비고: string;
}

/**
 * 엑셀 업로드용 파싱 결과 타입
 */
export interface ExcelUploadResult {
  success: boolean;
  data: UsageRecordFormData[];
  errors: string[];
  warnings: string[];
}

/**
 * 사용량 엑셀 템플릿 생성 및 다운로드
 */
export const downloadUsageTemplate = (
  activeRooms: PortvilleRoomWithResidency[],
  year: number,
  month: number
): void => {
  try {
    // 템플릿 데이터 생성
    const templateData: UsageExcelTemplate[] = activeRooms.map(room => ({
      호실번호: room.roomNumber,
      입주자명: room.currentResident?.name || '',
      소속: room.currentResident?.organization || '',
      개별전기사용량_kWh: '',
      개별수도사용량_톤: '',
      비고: ''
    }));

    // 워크북 생성
    const wb = XLSX.utils.book_new();
    
    // 워크시트 생성
    const ws = XLSX.utils.json_to_sheet(templateData);
    
    // 컬럼 너비 설정
    const colWidths = [
      { wch: 10 }, // 호실번호
      { wch: 15 }, // 입주자명
      { wch: 20 }, // 소속
      { wch: 18 }, // 개별전기사용량
      { wch: 18 }, // 개별수도사용량
      { wch: 20 }  // 비고
    ];
    ws['!cols'] = colWidths;

    // 워크시트를 워크북에 추가
    XLSX.utils.book_append_sheet(wb, ws, '사용량입력');

    // 파일명 생성
    const fileName = `사용량입력_${year}년${month}월_템플릿.xlsx`;
    
    // 엑셀 파일 생성 및 다운로드
    const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    
    FileSaver.saveAs(blob, fileName);
  } catch (error) {
    console.error('엑셀 템플릿 다운로드 실패:', error);
    throw new Error('엑셀 템플릿 다운로드에 실패했습니다.');
  }
};

/**
 * 엑셀 파일에서 사용량 데이터 파싱
 */
export const parseUsageExcel = (
  file: File,
  activeRooms: PortvilleRoomWithResidency[],
  year: number,
  month: number
): Promise<ExcelUploadResult> => {
  return new Promise((resolve) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const data = e.target?.result;
        if (!data) {
          resolve({
            success: false,
            data: [],
            errors: ['파일을 읽을 수 없습니다.'],
            warnings: []
          });
          return;
        }

        // 엑셀 파일 파싱
        const workbook = XLSX.read(data, { type: 'binary' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        
        // JSON으로 변환
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
        
        // 헤더 제거 (첫 번째 행)
        const dataRows = jsonData.slice(1);
        
        const result: ExcelUploadResult = {
          success: true,
          data: [],
          errors: [],
          warnings: []
        };

        // 호실번호로 매핑할 수 있는 맵 생성
        const roomMap = new Map<string, PortvilleRoomWithResidency>();
        activeRooms.forEach(room => {
          roomMap.set(room.roomNumber, room);
        });

        // 각 행 처리
        dataRows.forEach((row: unknown, index: number) => {
          const rowData = row as any[];
          const rowNumber = index + 2; // 엑셀 행 번호 (헤더 포함)
          
          try {
            // 필수 컬럼 확인
            if (!rowData[0]) {
              result.warnings.push(`${rowNumber}행: 호실번호가 비어있습니다.`);
              return;
            }

            const roomNumber = String(rowData[0]).trim();
            const room = roomMap.get(roomNumber);
            
            if (!room) {
              result.warnings.push(`${rowNumber}행: 호실번호 '${roomNumber}'를 찾을 수 없습니다.`);
              return;
            }

            // 사용량 데이터 파싱
            const electricityIndividualUsage = parseNumericValue(rowData[3], '개별전기사용량');
            const waterIndividualUsage = parseNumericValue(rowData[4], '개별수도사용량');

            // 최소 하나의 사용량은 입력되어야 함
            if (electricityIndividualUsage === undefined && waterIndividualUsage === undefined) {
              result.warnings.push(`${rowNumber}행: 최소 하나의 사용량은 입력해주세요.`);
              return;
            }

            // 유효성 검사 (음수 허용)
            if (electricityIndividualUsage !== undefined && electricityIndividualUsage < 0) {
              result.errors.push(`${rowNumber}행: 개별전기사용량은 0 이상이어야 합니다.`);
              return;
            }

            if (waterIndividualUsage !== undefined && waterIndividualUsage < 0) {
              result.errors.push(`${rowNumber}행: 개별수도사용량은 0 이상이어야 합니다.`);
              return;
            }

            // 사용량 데이터 생성
            const usageData: UsageRecordFormData = {
              roomId: room.id,
              roomNumber: room.roomNumber,
              year,
              month,
              electricityIndividualUsage,
              electricityCommonUsage: undefined, // 공용 사용량은 건물 전체에서 자동 계산
              waterIndividualUsage,
              waterCommonUsage: undefined // 공용 사용량은 건물 전체에서 자동 계산
            };

            result.data.push(usageData);

          } catch (error) {
            result.errors.push(`${rowNumber}행: 데이터 처리 중 오류가 발생했습니다.`);
            console.error(`행 ${rowNumber} 처리 오류:`, error);
          }
        });

        // 에러가 있으면 실패로 처리
        if (result.errors.length > 0) {
          result.success = false;
        }

        resolve(result);

      } catch (error) {
        console.error('엑셀 파일 파싱 실패:', error);
        resolve({
          success: false,
          data: [],
          errors: ['엑셀 파일 형식이 올바르지 않습니다.'],
          warnings: []
        });
      }
    };

    reader.onerror = () => {
      resolve({
        success: false,
        data: [],
        errors: ['파일 읽기에 실패했습니다.'],
        warnings: []
      });
    };

    reader.readAsBinaryString(file);
  });
};

/**
 * 숫자 값 파싱 (빈 문자열이나 null 처리)
 */
const parseNumericValue = (value: any, fieldName: string): number | undefined => {
  if (value === null || value === undefined || value === '') {
    return undefined;
  }

  const numValue = Number(value);
  if (isNaN(numValue)) {
    throw new Error(`${fieldName}이 숫자가 아닙니다.`);
  }

  return numValue;
};

/**
 * 기존 사용량 데이터를 엑셀 형식으로 내보내기
 */
export const exportUsageToExcel = (
  usageRecords: PortvilleUsageRecord[],
  activeRooms: PortvilleRoomWithResidency[],
  year: number,
  month: number
): void => {
  try {
    // 호실번호로 매핑할 수 있는 맵 생성
    const roomMap = new Map<string, PortvilleRoomWithResidency>();
    activeRooms.forEach(room => {
      roomMap.set(room.id, room);
    });

    // 내보내기 데이터 생성
    const exportData: UsageExcelTemplate[] = usageRecords.map(record => {
      const room = roomMap.get(record.roomId);
      return {
        호실번호: record.roomNumber,
        입주자명: room?.currentResident?.name || '',
        소속: room?.currentResident?.organization || '',
        개별전기사용량_kWh: record.electricityIndividualUsage || 0,
        개별수도사용량_톤: record.waterIndividualUsage || 0,
        비고: ''
      };
    });

    // 워크북 생성
    const wb = XLSX.utils.book_new();
    
    // 워크시트 생성
    const ws = XLSX.utils.json_to_sheet(exportData);
    
    // 컬럼 너비 설정
    const colWidths = [
      { wch: 10 }, // 호실번호
      { wch: 15 }, // 입주자명
      { wch: 20 }, // 소속
      { wch: 18 }, // 개별전기사용량
      { wch: 18 }, // 개별수도사용량
      { wch: 20 }  // 비고
    ];
    ws['!cols'] = colWidths;

    // 워크시트를 워크북에 추가
    XLSX.utils.book_append_sheet(wb, ws, '사용량데이터');

    // 파일명 생성
    const fileName = `사용량데이터_${year}년${month}월.xlsx`;
    
    // 엑셀 파일 생성 및 다운로드
    const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    
    FileSaver.saveAs(blob, fileName);
  } catch (error) {
    console.error('엑셀 내보내기 실패:', error);
    throw new Error('엑셀 내보내기에 실패했습니다.');
  }
};
